/*
  The game page's language bucket: which one is showing, and how a click changes it
  (docs/specs/pop-language-split.md D4-D6).

  What shows is DERIVED every render from the URL's `?lang`, the saved choice, Japan
  detection and the buckets this game has (resolveInitialBucket, lib/setLanguage). It is not
  state, so it settles on its own as catalogue pages arrive and new buckets appear.

  Only `choose` writes anything, and it is only ever called from the visitor's click:

    - `?lang=` goes in with `replace`, so flipping languages does not fill the back button,
      and every other search param is kept;
    - the choice is saved to localStorage (wrapped, D6).

  The D5 fallback writes nothing. A Japanese visitor on an English-only game sees English
  and is still Japanese on the next game.

  Lives here beside useMediaQuery rather than in lib/, whose modules touch no React.
*/

import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  LangBucket,
  detectJapan,
  readSavedBucket,
  resolveInitialBucket,
  saveBucket,
} from "lib/setLanguage";

export const LANG_PARAM = "lang";

export const useLanguageBucket = (
  present: readonly LangBucket[],
  cardsByBucket: Partial<Record<LangBucket, number>>,
  /* False while catalogue pages are still arriving (see resolveInitialBucket's `complete`). */
  complete = true,
  /* The buckets the game's own language list names (see resolveInitialBucket's `declared`). */
  declared?: readonly LangBucket[]
): [LangBucket, (bucket: LangBucket) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();
  /* Read once: storage and the browser's locale do not change under an open page. */
  const [saved, setSaved] = useState<LangBucket | null>(() => readSavedBucket());
  const isJapan = useMemo(() => detectJapan(), []);

  const bucket = resolveInitialBucket({
    urlParam: searchParams.get(LANG_PARAM),
    saved,
    isJapan,
    present,
    cardsByBucket,
    complete,
    declared,
  });

  const choose = useCallback(
    (next: LangBucket) => {
      const params = new URLSearchParams(searchParams);
      params.set(LANG_PARAM, next);
      setSearchParams(params, { replace: true });
      saveBucket(next);
      setSaved(next);
    },
    [searchParams, setSearchParams]
  );

  return [bucket, choose];
};
